import html2pdf from 'html2pdf.js';
import { COMPANY_INFO } from '@/lib/constants';
import type {
  EstimateResponse,
  AnalyzeResponse,
  ProjectInputs,
  RevenueModel,
  Platform,
  ItemPricing,
} from '@/types/estimation';

// ─── Labels ────────────────────────────────────────────────────────

const REVENUE_LABELS: Record<RevenueModel, string> = {
  subscription: 'Abonnement',
  ads: 'Publicité',
  freemium: 'Freemium',
  commission: 'Commission',
  one_time: 'Achat unique',
  usage_based: "Facturation à l'usage",
};

const PLATFORM_LABELS: Record<Platform, string> = {
  web: 'Web',
  ios: 'iOS',
  android: 'Android',
  desktop: 'Bureau',
};

// ─── Helpers ────────────────────────────────────────────────────────

const fmt = (n: number) => Math.round(n).toLocaleString('fr-FR');

const priceRangeStr = (p?: ItemPricing) =>
  p ? `${fmt(p.price_low)} € – ${fmt(p.price_high)} €` : '';

const esc = (s: string) =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

// ─── Colors ─────────────────────────────────────────────────────────

const C = {
  green500: '#16a34a',
  green600: '#15803d',
  green700: '#166534',
  green100: '#dcfce7',
  green200: '#bbf7d0',
  slate900: '#0f172a',
  slate800: '#1e293b',
  slate700: '#334155',
  slate600: '#475569',
  slate500: '#64748b',
  slate400: '#94a3b8',
  slate200: '#e2e8f0',
  slate100: '#f1f5f9',
  slate50: '#f8fafc',
  amber50: '#fffbeb',
  amber200: '#fde68a',
  amber800: '#92400e',
  red400: '#f87171',
  white: '#ffffff',
  fafafa: '#fafafa',
};

// ─── Convert local logo to base64 ──────────────────────────────────

async function logoToBase64(): Promise<string> {
  try {
    const resp = await fetch('/icon.png');
    const blob = await resp.blob();
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.readAsDataURL(blob);
    });
  } catch {
    return '';
  }
}

// ─── Build Inline-Styled HTML ───────────────────────────────────────

export async function generateEstimationPdf(
  estimate: EstimateResponse,
  analysis: AnalyzeResponse | null,
  inputs: ProjectInputs,
): Promise<Blob> {
  const logoDataUrl = await logoToBase64();
  const pricing = estimate.pricing;
  const sf = estimate.selected_features;
  const today = new Date().toLocaleDateString('fr-FR', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  // ── Feature cards ──

  const buildFeatureCard = (
    name: string,
    description?: string | null,
    explanation?: string | null,
    price?: ItemPricing,
    subFeatures?: { name: string; explanation?: string | null }[],
  ) => {
    const priceStr = priceRangeStr(price);
    const subsHtml = subFeatures
      ?.map(
        (s) =>
          `<li style="font-size:10px;color:${C.slate600};margin-bottom:3px;line-height:1.5;">
            ${esc(s.name)}
            ${s.explanation ? `<div style="font-size:9px;color:${C.slate400};font-style:italic;margin-top:1px;">${esc(s.explanation)}</div>` : ''}
          </li>`,
      )
      .join('') || '';

    return `
      <div style="border:1px solid ${C.slate200};border-radius:8px;padding:10px 12px;background:${C.fafafa};margin-bottom:8px;">
        <div style="display:flex;justify-content:space-between;align-items:center;gap:8px;">
          <span style="font-weight:700;font-size:11px;color:${C.slate900};">${esc(name)}</span>
          ${priceStr ? `<span style="font-size:10px;font-weight:600;color:${C.green500};white-space:nowrap;">${priceStr}</span>` : ''}
        </div>
        ${description ? `<div style="font-size:10px;color:${C.slate500};margin-top:3px;">${esc(description)}</div>` : ''}
        ${explanation ? `<div style="font-size:9.5px;color:${C.slate400};font-style:italic;margin-top:3px;">${esc(explanation)}</div>` : ''}
        ${subsHtml ? `<ul style="margin:6px 0 0 14px;padding:0;list-style:disc;">${subsHtml}</ul>` : ''}
      </div>`;
  };

  let featuresHtml = '';
  sf?.matched_features?.forEach((f) => {
    featuresHtml += buildFeatureCard(
      f.name,
      f.description,
      f.explanation,
      f.pricing,
      f.sub_features?.map((s) => ({ name: s.name })),
    );
  });
  sf?.generated_features?.forEach((f) => {
    featuresHtml += buildFeatureCard(
      f.name,
      f.description,
      f.explanation,
      f.pricing,
      f.sub_features?.map((s) => ({ name: s.name, explanation: s.explanation })),
    );
  });

  // ── Addons ──

  let addonsHtml = '';
  sf?.addons?.forEach((a) => {
    const price = priceRangeStr(a.pricing);
    addonsHtml += `
      <div style="display:flex;justify-content:space-between;align-items:center;background:${C.slate50};border:1px solid ${C.slate200};border-radius:8px;padding:8px 12px;margin-bottom:6px;font-size:10.5px;">
        <div>
          <strong style="color:${C.slate900};">${esc(a.name)}</strong>
          ${a.description ? `<span style="color:${C.slate500};font-weight:400;"> — ${esc(a.description)}</span>` : ''}
        </div>
        ${price ? `<span style="font-size:10px;font-weight:600;color:${C.green500};white-space:nowrap;">${price}</span>` : ''}
      </div>`;
  });
  sf?.generated_addons?.forEach((a) => {
    const price = priceRangeStr(a.pricing);
    const subHtml = a.sub_items
      ?.map(
        (si) =>
          `<li style="font-size:10px;color:${C.slate600};margin-bottom:2px;">${esc(si.name)}</li>`,
      )
      .join('') || '';
    addonsHtml += `
      <div style="background:${C.slate50};border:1px solid ${C.slate200};border-radius:8px;padding:8px 12px;margin-bottom:6px;">
        <div style="display:flex;justify-content:space-between;align-items:center;">
          <div>
            <strong style="font-size:10.5px;color:${C.slate900};">${esc(a.name)}</strong>
            ${a.description ? `<span style="font-size:10px;color:${C.slate500};font-weight:400;"> — ${esc(a.description)}</span>` : ''}
          </div>
          ${price ? `<span style="font-size:10px;font-weight:600;color:${C.green500};white-space:nowrap;">${price}</span>` : ''}
        </div>
        ${a.explanation ? `<div style="font-size:9.5px;color:${C.slate400};font-style:italic;margin-top:4px;">${esc(a.explanation)}</div>` : ''}
        ${subHtml ? `<ul style="margin:4px 0 0 14px;padding:0;list-style:disc;">${subHtml}</ul>` : ''}
      </div>`;
  });

  // ── Timeline ──

  const timelineRows = estimate.timeline_phases
    .map(
      (p, i) => `
      <tr>
        <td style="padding:6px 10px;border-bottom:1px solid ${C.slate100};text-align:center;font-weight:700;color:${C.green500};width:24px;">${i + 1}</td>
        <td style="padding:6px 10px;border-bottom:1px solid ${C.slate100};color:${C.slate700};">${esc(p.name)}</td>
        <td style="padding:6px 10px;border-bottom:1px solid ${C.slate100};text-align:right;font-weight:600;color:${C.green500};white-space:nowrap;">${p.duration_weeks} sem</td>
      </tr>`,
    )
    .join('');

  // ── Platform / Revenue tags ──

  const tagStyle = `display:inline-block;background:${C.green100};color:${C.green700};font-size:9px;font-weight:600;padding:3px 10px;border-radius:20px;margin-right:5px;margin-bottom:4px;`;
  const platformTags = inputs.platforms
    .map((p) => `<span style="${tagStyle}">${PLATFORM_LABELS[p] || p}</span>`)
    .join('');
  const revenueTags = inputs.revenueModels
    .map((r) => `<span style="${tagStyle}">${REVENUE_LABELS[r] || r}</span>`)
    .join('');

  // ── Section heading helper ──

  const sectionTitle = (text: string) =>
    `<div style="font-size:13px;font-weight:700;color:${C.slate900};margin-bottom:10px;padding-bottom:6px;border-bottom:2px solid ${C.green100};display:flex;align-items:center;gap:6px;">
      <span style="display:inline-block;width:4px;height:16px;background:${C.green500};border-radius:2px;"></span>
      ${text}
    </div>`;

  // ═══ ASSEMBLE FULL PAGE ═══

  const html = `
<div style="width:718px;font-family:'Segoe UI',system-ui,-apple-system,sans-serif;color:${C.slate800};font-size:11px;line-height:1.55;background:${C.white};">

  <!-- ═══ HEADER ═══ -->
  <div style="background:linear-gradient(135deg,${C.green500},${C.green600});color:${C.white};padding:28px 32px 24px;display:flex;align-items:center;justify-content:space-between;">
    <div style="display:flex;align-items:center;gap:14px;">
      ${logoDataUrl ? `<img src="${logoDataUrl}" style="width:44px;height:44px;border-radius:10px;" />` : ''}
      <div>
        <div style="font-size:22px;font-weight:800;letter-spacing:-0.3px;">Orcsys</div>
        <div style="font-size:11px;opacity:0.85;margin-top:2px;">Estimation de projet</div>
      </div>
    </div>
    <div style="text-align:right;font-size:10px;opacity:0.9;line-height:1.7;">
      ${COMPANY_INFO.phone}<br>
      ${COMPANY_INFO.email}<br>
      ${COMPANY_INFO.website}
    </div>
  </div>

  <!-- ═══ CONTENT ═══ -->
  <div style="padding:24px 32px 20px;">

    <div style="font-size:18px;font-weight:700;color:${C.slate900};margin-bottom:4px;">Votre estimation personnalisée</div>
    <div style="font-size:10px;color:${C.slate500};margin-bottom:20px;">${today} — Réf. ${estimate.id.slice(0, 8).toUpperCase()}</div>

    <!-- ── Price ── -->
    ${pricing ? `
    <div style="border-radius:12px;overflow:hidden;margin-bottom:24px;">
      <div style="background:${C.slate800};color:${C.white};text-align:center;padding:14px 20px;">
        <div style="font-size:10px;color:${C.slate400};margin-bottom:2px;">Tarif moyen constaté en agence</div>
        <div style="font-size:22px;font-weight:700;text-decoration:line-through;text-decoration-color:${C.red400};">${fmt(pricing.market_rate)} €</div>
      </div>
      <div style="background:linear-gradient(135deg,${C.green500},${C.green600});color:${C.white};text-align:center;padding:22px 20px;">
        <div style="font-size:10px;color:${C.green200};margin-bottom:6px;">Si réalisé par notre équipe</div>
        <div style="font-size:28px;font-weight:800;letter-spacing:-0.5px;">
          ${fmt(pricing.price_low)} €
          <span style="font-size:18px;opacity:0.5;margin:0 10px;">—</span>
          ${fmt(pricing.price_high)} €
        </div>
        <div style="font-size:10px;color:${C.green200};margin-top:8px;">Fourchette estimée selon la complexité du projet</div>
        <div style="margin-top:14px;padding-top:12px;border-top:1px solid rgba(255,255,255,0.2);font-size:12px;font-weight:600;">
          ≈ ${estimate.total_duration_weeks} semaines de développement
        </div>
      </div>
    </div>
    ` : `
    <div style="background:linear-gradient(135deg,${C.green500},${C.green600});color:${C.white};text-align:center;padding:24px 20px;border-radius:12px;margin-bottom:24px;">
      <div style="font-size:10px;color:${C.green200};margin-bottom:6px;">Estimation</div>
      <div style="font-size:32px;font-weight:800;">${fmt(estimate.price)} €</div>
      <div style="margin-top:14px;padding-top:12px;border-top:1px solid rgba(255,255,255,0.2);font-size:12px;font-weight:600;">
        ≈ ${estimate.total_duration_weeks} semaines
      </div>
    </div>
    `}

    <!-- ── Project details ── -->
    <div style="margin-bottom:20px;">
      ${sectionTitle('Détails du projet')}
      <div style="margin-bottom:8px;">${platformTags}${revenueTags}</div>
      <div style="font-size:10.5px;color:${C.slate600};line-height:1.6;margin-bottom:6px;">${esc(inputs.projectDescription)}</div>
      ${analysis ? `<div style="font-size:10px;color:${C.slate400};">Catégorie : <strong style="color:${C.slate600};">${esc(analysis.project_category)}</strong></div>` : ''}
    </div>

    <!-- ── Features ── -->
    ${featuresHtml ? `
    <div style="margin-bottom:20px;">
      ${sectionTitle('Fonctionnalités incluses')}
      ${featuresHtml}
    </div>
    ` : ''}

    <!-- ── Addons ── -->
    ${addonsHtml ? `
    <div style="margin-bottom:20px;">
      ${sectionTitle('Extensions')}
      ${addonsHtml}
    </div>
    ` : ''}

    <!-- ── Timeline ── -->
    ${timelineRows ? `
    <div style="margin-bottom:20px;">
      ${sectionTitle('Calendrier prévisionnel')}
      <table style="width:100%;border-collapse:collapse;font-size:10.5px;margin-bottom:10px;">
        <thead>
          <tr>
            <th style="background:${C.slate50};font-weight:600;text-align:left;padding:6px 10px;border-bottom:2px solid ${C.slate200};color:${C.slate600};font-size:9.5px;text-transform:uppercase;letter-spacing:0.5px;">#</th>
            <th style="background:${C.slate50};font-weight:600;text-align:left;padding:6px 10px;border-bottom:2px solid ${C.slate200};color:${C.slate600};font-size:9.5px;text-transform:uppercase;letter-spacing:0.5px;">Phase</th>
            <th style="background:${C.slate50};font-weight:600;text-align:right;padding:6px 10px;border-bottom:2px solid ${C.slate200};color:${C.slate600};font-size:9.5px;text-transform:uppercase;letter-spacing:0.5px;">Durée</th>
          </tr>
        </thead>
        <tbody>${timelineRows}</tbody>
      </table>
      <div style="text-align:right;font-size:11px;font-weight:700;color:${C.slate900};margin-top:4px;">
        Total : <span style="color:${C.green500};">≈ ${estimate.total_duration_weeks} semaines</span>
      </div>
    </div>
    ` : ''}

    <!-- ── Disclaimer ── -->
    <div style="background:${C.amber50};border:1px solid ${C.amber200};border-radius:8px;padding:12px 14px;font-size:9.5px;color:${C.amber800};line-height:1.55;margin-bottom:12px;">
      <strong style="display:block;margin-bottom:3px;font-size:10px;">⚠ Ceci est une estimation indicative, pas un devis</strong>
      Les montants et délais présentés sont des estimations basées sur les informations fournies.
      Ils ne constituent pas un devis contractuel. Pour obtenir un devis personnalisé et détaillé, contactez notre équipe.
    </div>
  </div>

  <!-- ═══ CTA FOOTER ═══ -->
  <div style="background:linear-gradient(135deg,${C.green500},${C.green600});color:${C.white};padding:28px 32px;text-align:center;">
    <div style="font-size:16px;font-weight:700;margin-bottom:6px;">Prêt à concrétiser votre projet ?</div>
    <div style="font-size:10.5px;color:${C.green200};margin-bottom:16px;line-height:1.6;">
      Contactez-nous pour discuter de votre projet et obtenir un devis détaillé.<br>
      Nous vous répondrons sous 24h ouvrées.
    </div>
    <table style="margin:0 auto;border-collapse:separate;border-spacing:16px 0;">
      <tr>
        <td style="padding:10px 24px;border-radius:8px;font-weight:700;font-size:11px;background:${C.white};color:${C.green500};text-align:center;white-space:nowrap;">
          Appelez-nous : ${COMPANY_INFO.phone}
        </td>
        <td style="padding:10px 24px;border-radius:8px;font-weight:700;font-size:11px;border:2px solid ${C.white};color:${C.white};background:transparent;text-align:center;white-space:nowrap;">
          Prenez RDV : ${COMPANY_INFO.website}/booking
        </td>
      </tr>
    </table>
  </div>

  <!-- ═══ LEGAL FOOTER ═══ -->
  <div style="text-align:center;font-size:8.5px;color:${C.slate400};padding:10px 32px;border-top:1px solid ${C.slate200};">
    © ${new Date().getFullYear()} ${COMPANY_INFO.name}
  </div>

</div>`;

  // ── Render off-screen and generate PDF blob ──

  const container = document.createElement('div');
  container.style.position = 'fixed';
  container.style.left = '0';
  container.style.top = '0';
  container.style.width = '718px';
  container.style.zIndex = '-9999';
  container.style.pointerEvents = 'none';
  container.style.overflow = 'visible';
  container.innerHTML = html;
  document.body.appendChild(container);

  // Give browser a tick to layout
  await new Promise((r) => setTimeout(r, 300));

  const target = container.firstElementChild as HTMLElement;
  const filename = `estimation-orcsys-${estimate.id.slice(0, 8)}.pdf`;

  const pdfOpts = {
    margin: [8, 10, 8, 10],
    filename,
    image: { type: 'jpeg', quality: 0.98 },
    html2canvas: {
      scale: 2,
      useCORS: true,
      backgroundColor: C.white,
      width: 718,
      scrollX: 0,
      scrollY: 0,
      windowWidth: 718,
    },
    jsPDF: {
      unit: 'mm',
      format: 'a4',
      orientation: 'portrait' as const,
    },
    pagebreak: { mode: ['avoid-all', 'css', 'legacy'] },
  };

  try {
    // Generate as blob
    const blob: Blob = await html2pdf().set(pdfOpts).from(target).outputPdf('blob');

    // Also trigger browser download
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    return blob;
  } finally {
    document.body.removeChild(container);
  }
}
